import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Image,
  ActivityIndicator,
  Platform,
  Animated,
  Easing,
  type ImageSourcePropType,
} from "react-native";
import {
  ChevronLeft,
  Snowflake,
  MapPin,
  Calendar,
  Shield,
  ChevronRight,
  ChevronDown,
  Check,
  Droplet,
  Egg,
  FileText,
  AlertCircle,
  Info,
  Plus,
  Sparkles,
  Truck,
  Heart,
  Upload,
  type LucideIcon,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { EMBER } from "../../onboarding/theme";
import type { CareStackParamList } from "../types";
import { useSmartBack } from "../hooks/useSmartBack";
import { useCycleNavigation } from "../hooks/useCycleNavigation";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  CardSubtitleText,
  CardTitleText,
  ListRowSkeleton,
  SectionTitle,
  ClinicalDataStatus,
  Dialog,
  DialogHeader,
  DialogPrimaryButton,
  SoftSecondaryButton,
} from "@louise/moon-ui";
import StatusBadge from "../components/StatusBadge";
import ScreenHeader from "../components/ScreenHeader";
import { OrganicMiniIcon } from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { Hero } from "@src/packages/homepage/plan/components/hero/Hero";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import {
  getRemainingOocyteCohortCount,
  useOocyteCohorts,
  useDeleteSpecimen,
  useSpecimens,
} from "@src/packages/care/cryovault/useSpecimens";
import {
  useDocuments,
  type Document,
} from "@src/packages/documents/hooks/useDocuments";
import { AddSpecimenModal } from "./care/modals/AddSpecimenModal";
import type { FrozenPrefill } from "./care/modals/MoonStartTreatmentModal";
import { useStartTreatmentController } from "../StartTreatmentController";
import { DocumentRow } from "./documents/DocumentRow";
import { useLocale, useTranslations } from "@src/packages/i18n";
import { formatDate } from "@src/packages/i18n/formatters";
import type { Locale } from "@src/packages/i18n/locale";
import {
  CryoSpecimenDetailModal,
  buildCryoSpecimenLabel,
  mapApiOocyteCohortsToCryoDetails,
  mapApiSpecimensToCryoDetails,
  type CryoSpecimenDetail,
} from "../components/CryoSpecimenDetailModal";
import {
  computeCombinedPotential,
  computeEggPotential,
  computeEmbryoPotential,
  type CombinedPotentialStep,
  type PotentialStep,
} from "./cryoPotential";
import {
  OutcomeDotFunnelRows,
  type OutcomeDotFunnelStage,
} from "@src/packages/homepage/plan/components/OutcomeDotFunnelRows";
import {
  FunnelScopeTabs,
  type FunnelScopeTabOption,
} from "@src/packages/homepage/plan/components/FunnelScopeTabs";
import { getDevClinicalDataState } from "@src/packages/care/dev-clinical-data-state";
import {
  buildDevCryoVaultSpecimens,
  buildDevSpecimenModalSpecimens,
  getDevAddEmbryosPrefill,
  getDevCryoVaultScenario,
  shouldOpenDevAddEmbryosSurface,
} from "../dev/cryoVaultFixture";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// ── Types ──

type SpecimenType = "embryo" | "oocyte" | "sperm";

type Specimen = CryoSpecimenDetail;

/** The API mapper converts unverified storage into a non-stored status. */
function isVerifiedStoredSpecimen(specimen: Specimen): boolean {
  return (
    specimen.status === "stored" ||
    (specimen.oocyteRecordKind === "cohort" &&
      specimen.status === "partially_used" &&
      Boolean(specimen.facility && specimen.frozenDate))
  );
}

const DOCUMENT_CYCLE_TAG_PREFIX = "cycle:";
const DOCUMENT_SPECIMEN_TAG_PREFIX = "specimen:";

/**
 * These tags are the explicit document/entity relationships currently
 * available to the client. Category tags are intentionally not treated as
 * CryoVault links. When a record exposes multiple cycle relationships, keep
 * every persisted relationship in the document filter rather than collapsing
 * the graph to the legacy scalar projections.
 */
function getCryoDocumentRelationTags(specimens: Specimen[]): Set<string> {
  const tags = new Set<string>();
  for (const specimen of specimens) {
    tags.add(`${DOCUMENT_SPECIMEN_TAG_PREFIX}${specimen.id}`);
    const explicitCycleIds = specimen.cycleRelationships?.map(
      (relationship) => relationship.cycleId,
    ) ?? [specimen.cycleId, specimen.useCycleId, specimen.transferCycleId];
    for (const cycleId of explicitCycleIds) {
      if (cycleId) tags.add(`${DOCUMENT_CYCLE_TAG_PREFIX}${cycleId}`);
    }
  }
  return tags;
}

function isCryoRelatedDocument(
  document: Document,
  relationTags: Set<string>,
): boolean {
  return document.tags.some((tag) => relationTags.has(tag));
}

function getDocumentRowStatus(
  document: Document,
): "on-file" | "requested" | undefined {
  if (document.uploads.some((upload) => upload.uploadStatus === "pending")) {
    return "requested";
  }
  if (document.uploads.some((upload) => upload.uploadStatus === "uploaded")) {
    return "on-file";
  }
  return undefined;
}

type CryoUploadFile = {
  name: string;
  uri: string;
  mimeType?: string | null;
  objectUrl?: string;
  size?: number;
};

type WebDragEvent = DragEvent;
type WebDropZoneTarget = View & {
  addEventListener: HTMLElement["addEventListener"];
  removeEventListener: HTMLElement["removeEventListener"];
};

type FacilityInfo = {
  name: string;
  specimenCount: number;
  storageStartDate?: string | null;
  nextPaymentDate?: string | null;
  annualCostCents?: number | null;
};

type RetrievalFunnel = {
  cycleId?: string;
  cycleLabel: string;
  retrieved: number;
  mature?: number | null;
  fertilized?: number | null;
  blastocyst?: number | null;
  pgtTested?: number | null;
  euploid?: number | null;
  frozen: number;
};

type DispositionStatus = "filed" | "missing" | "expiring_soon";

// ── Config ──

const SPECIMEN_CONFIG: Record<
  SpecimenType,
  {
    singularKey: string;
    pluralKey: string;
    color: string;
    sectionIcon: LucideIcon;
  }
> = {
  embryo: {
    singularKey: "cryo_vault.specimen_type.embryo_singular",
    pluralKey: "cryo_vault.specimen_type.embryo_plural",
    color: "#38BDF8",
    sectionIcon: Sparkles,
  },
  oocyte: {
    singularKey: "cryo_vault.specimen_type.egg_singular",
    pluralKey: "cryo_vault.specimen_type.egg_plural",
    color: EMBER.colors.gold,
    sectionIcon: Egg,
  },
  sperm: {
    singularKey: "cryo_vault.specimen_type.sperm_sample_singular",
    pluralKey: "cryo_vault.specimen_type.sperm_sample_plural",
    color: "#86EFAC",
    sectionIcon: Droplet,
  },
};

const PGT_CONFIG: Record<string, { labelKey: string; color: string }> = {
  euploid: { labelKey: "cryo_vault.pgt.euploid", color: EMBER.colors.green },
  aneuploid: {
    labelKey: "cryo_vault.pgt.aneuploid",
    color: EMBER.colors.coral,
  },
  mosaic: { labelKey: "cryo_vault.pgt.mosaic", color: EMBER.colors.gold },
  untested: {
    labelKey: "cryo_vault.pgt.untested",
    color: EMBER.colors.textFaint,
  },
};

const OOCYTE_SAMPLE_IMAGE =
  require("../../assets/images/3d/oocyte-sample.png") as ImageSourcePropType;

const MAX_VISIBLE_OOCYTE_UNITS = 40;

// ── Shared card style with reduced radius ──

const CARD_STYLE = {
  ...GLASS_CARD_STYLE,
  borderRadius: 16,
};

// ── Components ──

function getOriginalFrozenOocyteCount(specimen: Specimen): number {
  if (specimen.type !== "oocyte") return 0;
  if (specimen.oocyteRecordKind !== "cohort") return 1;
  return Math.max(0, specimen.numberFrozen ?? 1);
}

function getUsedOocyteCount(specimen: Specimen): number {
  if (specimen.type !== "oocyte") return 0;
  const total = getOriginalFrozenOocyteCount(specimen);
  return Math.max(0, total - getRemainingFrozenOocyteCount(specimen));
}

function getRemainingFrozenOocyteCount(specimen: Specimen): number {
  if (specimen.type !== "oocyte") return 0;
  return getRemainingOocyteCohortCount(
    specimen.oocyteRecordKind === "cohort" ? specimen.numberFrozen : null,
    specimen.numberThawed,
    1,
  );
}

function getSpecimenInventoryCount(specimen: Specimen): number {
  if (specimen.type === "oocyte") {
    return getRemainingFrozenOocyteCount(specimen);
  }
  return 1;
}

function getSpecimenInventoryTotal(specimens: Specimen[]): number {
  return specimens.reduce(
    (sum, specimen) => sum + getSpecimenInventoryCount(specimen),
    0,
  );
}

function getOocyteCohortInventoryTitle(
  t: ReturnType<typeof useTranslations>,
  specimen: Specimen,
): string {
  const used = getUsedOocyteCount(specimen);
  const remaining = getRemainingFrozenOocyteCount(specimen);
  if (used > 0) {
    return t("cryo_vault.grouped_inventory.egg_cohort_title_used", {
      params: {
        remaining: String(remaining),
        used: String(used),
        label: specimen.label,
      },
    });
  }
  return t("cryo_vault.grouped_inventory.egg_cohort_title_frozen", {
    params: {
      count: String(remaining),
      label: specimen.label,
    },
  });
}

function CryoSummary({ specimens }: { specimens: Specimen[] }) {
  const t = useTranslations();
  const verifiedSpecimens = specimens.filter(isVerifiedStoredSpecimen);
  const embryoCount = verifiedSpecimens.filter(
    (s) => s.type === "embryo",
  ).length;
  const eggCount = getSpecimenInventoryTotal(
    verifiedSpecimens.filter((s) => s.type === "oocyte"),
  );
  const spermCount = verifiedSpecimens.filter((s) => s.type === "sperm").length;
  const total = embryoCount + eggCount + spermCount;
  const facilityCount = new Set(
    verifiedSpecimens.map((s) => s.facility).filter(Boolean),
  ).size;

  if (total === 0) return null;

  const breakdownParts: string[] = [];
  if (embryoCount > 0) {
    breakdownParts.push(
      `${embryoCount} ${
        embryoCount === 1
          ? t("cryo_vault.specimen_type.embryo_singular")
          : t("cryo_vault.specimen_type.embryo_plural")
      }`,
    );
  }
  if (eggCount > 0) {
    breakdownParts.push(
      `${eggCount} ${
        eggCount === 1
          ? t("cryo_vault.specimen_type.egg_singular")
          : t("cryo_vault.specimen_type.egg_plural")
      }`,
    );
  }
  if (spermCount > 0) {
    breakdownParts.push(
      `${spermCount} ${
        spermCount === 1
          ? t("cryo_vault.specimen_type.sperm_sample_singular")
          : t("cryo_vault.specimen_type.sperm_sample_plural")
      }`,
    );
  }
  const breakdown = breakdownParts.join(" · ");
  const facilityLabel =
    facilityCount === 1
      ? t("cryo_vault.hero.facility_one")
      : t("cryo_vault.hero.facility_other", {
          params: { count: String(facilityCount) },
        });

  return (
    <Hero
      eyebrow={{ label: t("cryo_vault.hero.eyebrow"), icon: Snowflake }}
      title={
        total === 1
          ? t("cryo_vault.hero.title_singular", {
              params: { count: String(total) },
            })
          : t("cryo_vault.hero.title_plural", {
              params: { count: String(total) },
            })
      }
      summary={`${breakdown} ${facilityLabel}`}
    />
  );
}

/** Specimen icon — real embryo image if present, otherwise organic illustration */
function SpecimenIcon({
  specimen,
  size,
}: {
  specimen: Specimen;
  size: number;
}) {
  if (specimen.type === "embryo") {
    const realImageUrl = specimen.mediaUrls?.[0];
    if (realImageUrl) {
      return (
        <Image
          source={{ uri: realImageUrl }}
          style={{
            width: size,
            height: size,
            borderRadius: size / 3,
          }}
          resizeMode="contain"
        />
      );
    }
    return <OrganicMiniIcon kind="genetics" size={size} />;
  }
  if (specimen.type === "oocyte") {
    return <OrganicMiniIcon kind="eggs" size={size} />;
  }
  return <OrganicMiniIcon kind="sperm" size={size} />;
}

/** Vertical specimen card for 2-column grid */
function SpecimenTile({ specimen }: { specimen: Specimen }) {
  const t = useTranslations();
  const { locale } = useLocale();
  const pgt = specimen.pgtResult ? PGT_CONFIG[specimen.pgtResult] : null;
  const frozenFormatted = formatSpecimenDate(specimen.frozenDate, locale);
  const subtitleParts: string[] = [];
  const storageStatus = getStorageStatusLabel(t, specimen);
  if (storageStatus) subtitleParts.push(storageStatus);
  if (specimen.grade) {
    subtitleParts.push(
      t("cryo_vault.grade_prefix", { params: { grade: specimen.grade } }),
    );
  }
  if (specimen.motility) {
    subtitleParts.push(
      t("cryo_vault.motility_suffix", { params: { value: specimen.motility } }),
    );
  }
  if (frozenFormatted) {
    subtitleParts.push(frozenFormatted);
  }
  const subtitle = subtitleParts.join(" · ");

  return (
    <View
      style={{
        flex: 1,
        ...CARD_STYLE,
        minHeight: 118,
        padding: 14,
        justifyContent: "space-between",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <SpecimenIcon specimen={specimen} size={36} />
        <ChevronRight
          size={19}
          color={EMBER.colors.textLight}
          strokeWidth={1.7}
        />
      </View>

      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <CardTitleText numberOfLines={1}>{specimen.label}</CardTitleText>
          {subtitle ? (
            <CardSubtitleText numberOfLines={1}>{subtitle}</CardSubtitleText>
          ) : null}
        </View>
        {pgt ? (
          <StatusBadge
            label={t(pgt.labelKey)}
            tone={
              specimen.pgtResult === "euploid"
                ? "green"
                : specimen.pgtResult === "aneuploid"
                  ? "coral"
                  : specimen.pgtResult === "mosaic"
                    ? "gold"
                    : "neutral"
            }
          />
        ) : null}
      </View>
    </View>
  );
}

function formatSpecimenDate(
  raw: string | null | undefined,
  locale: Locale,
): string | null {
  if (!raw) return null;
  const datePart = raw.split("T")[0];
  const parsed = new Date(`${datePart}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return raw;
  return formatDate(parsed, locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** 2-column grid of specimen tiles with optional batch indicators */
function SpecimenGrid({
  items,
  onSelect,
}: {
  items: Specimen[];
  onSelect: (id: string) => void;
}) {
  const capturePress = usePressCapture("SpecimenGrid");
  // Check if there are multiple batches (for batch dot indicator)
  const batchIds = [
    ...new Set(items.map((s) => s.batchId).filter(Boolean)),
  ] as string[];
  const showBatchDots = batchIds.length > 1;
  const batchColorMap: Record<string, string> = {};
  const batchColors = [
    EMBER.colors.cryo,
    EMBER.colors.gold,
    EMBER.colors.green,
    EMBER.colors.coral,
  ];
  batchIds.forEach((id, i) => {
    batchColorMap[id] = batchColors[i % batchColors.length] ?? "#38BDF8";
  });

  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
      {items.map((specimen) => (
        <Pressable
          key={specimen.id}
          onPress={() => {
            capturePress(pressLabel(specimen.label), "select_specimen");
            onSelect(specimen.id);
          }}
          style={{
            flexGrow: 1,
            flexBasis: "22%",
            minWidth: 130,
            position: "relative",
          }}
        >
          <SpecimenTile specimen={specimen} />
          {/* Batch dot */}
          {showBatchDots && specimen.batchId && (
            <View
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                width: 6,
                height: 6,
                borderRadius: 3,
                backgroundColor:
                  batchColorMap[specimen.batchId] ?? EMBER.colors.textFaint,
              }}
            />
          )}
        </Pressable>
      ))}
    </View>
  );
}

type SpecimenDateBatch = {
  /** Explicit cohort/batch identity. Individual rows never merge by date. */
  identity: string;
  frozenDate: string | null;
  items: Specimen[];
};

type SpecimenFacilityGroup = {
  facility: string;
  batches: SpecimenDateBatch[];
};

function getSpecimenCountLabel(
  t: ReturnType<typeof useTranslations>,
  specimenType: SpecimenType,
  count: number,
): string {
  const config = SPECIMEN_CONFIG[specimenType];
  return `${count} ${count === 1 ? t(config.singularKey) : t(config.pluralKey)}`;
}

function getStorageStatusLabel(
  t: ReturnType<typeof useTranslations>,
  specimen: Specimen,
): string | null {
  if (specimen.status === "storage_pending") {
    return t("cryo_vault.modal.storage_pending");
  }
  if (specimen.status === "storage_unverified") {
    return t("cryo_vault.modal.storage_unverified");
  }
  return null;
}

function getNestedSpecimenDetailRows(
  t: ReturnType<typeof useTranslations>,
  specimen: Specimen,
): string[] {
  const storageStatus = getStorageStatusLabel(t, specimen);
  if (specimen.type === "embryo") {
    const rows: string[] = storageStatus ? [storageStatus] : [];
    if (specimen.grade) {
      rows.push(
        t("cryo_vault.grade_prefix", { params: { grade: specimen.grade } }),
      );
    }

    const outcomeParts: string[] = [];
    const pgt = specimen.pgtResult ? PGT_CONFIG[specimen.pgtResult] : null;
    if (pgt) {
      outcomeParts.push(t(pgt.labelKey));
    }
    if (specimen.transferCandidate) {
      outcomeParts.push(t("cryo_vault.top_pick"));
    }
    if (outcomeParts.length > 0) {
      rows.push(outcomeParts.join(" · "));
    }
    return rows;
  }

  if (specimen.type === "sperm") {
    const rows: string[] = storageStatus ? [storageStatus] : [];
    if (specimen.motility) {
      rows.push(
        t("cryo_vault.motility_suffix", {
          params: { value: specimen.motility },
        }),
      );
    }

    const labParts = [specimen.concentration, specimen.morphology].filter(
      (part): part is string => Boolean(part),
    );
    if (labParts.length > 0) {
      rows.push(labParts.join(" · "));
    }
    return rows;
  }

  return storageStatus ? [storageStatus] : [];
}

function groupSpecimensByFacilityAndDate(
  items: Specimen[],
): SpecimenFacilityGroup[] {
  const facilityGroups = items.reduce<SpecimenFacilityGroup[]>(
    (acc, specimen) => {
      const facilityKey = specimen.facility ?? "";
      let group = acc.find((g) => g.facility === facilityKey);
      if (!group) {
        group = { facility: facilityKey, batches: [] };
        acc.push(group);
      }

      const identity = specimen.batchId
        ? `batch:${specimen.batchId}`
        : `specimen:${specimen.id}`;
      const batchKey = specimen.frozenDate ?? null;
      const batch = group.batches.find((b) => b.identity === identity);
      if (batch) {
        batch.items.push(specimen);
      } else {
        group.batches.push({
          identity,
          frozenDate: batchKey,
          items: [specimen],
        });
      }

      return acc;
    },
    [],
  );

  facilityGroups.sort((a, b) => a.facility.localeCompare(b.facility));
  facilityGroups.forEach((group) => {
    group.batches.sort((a, b) =>
      (b.frozenDate ?? "").localeCompare(a.frozenDate ?? ""),
    );
  });

  return facilityGroups;
}

function OocyteUnitGrid({ specimen }: { specimen: Specimen }) {
  const originalFrozen = getOriginalFrozenOocyteCount(specimen);
  const remaining = getRemainingFrozenOocyteCount(specimen);
  const visibleCount = Math.min(originalFrozen, MAX_VISIBLE_OOCYTE_UNITS);
  const hiddenCount = Math.max(0, originalFrozen - visibleCount);

  return (
    <View>
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {Array.from({ length: visibleCount }, (_, index) => {
          const isRemaining = index < remaining;
          return (
            <Image
              key={`${specimen.id}-egg-unit-${index}`}
              source={OOCYTE_SAMPLE_IMAGE}
              resizeMode="contain"
              style={{
                width: 24,
                height: 29,
                opacity: isRemaining ? 0.96 : 0.28,
              }}
            />
          );
        })}
        {hiddenCount > 0 ? (
          <View
            style={{
              minWidth: 34,
              height: 21,
              borderRadius: 10,
              backgroundColor: "rgba(255,255,255,0.58)",
              borderWidth: 1,
              borderColor: "rgba(92,52,32,0.12)",
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 6,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 10.5,
                lineHeight: 12,
                fontWeight: "800",
                color: EMBER.colors.textLight,
              }}
            >
              +{hiddenCount}
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );
}

function NestedSpecimenTile({
  specimen,
  onPress,
}: {
  specimen: Specimen;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("NestedSpecimenTile");
  const t = useTranslations();
  const detailRows = getNestedSpecimenDetailRows(t, specimen).slice(0, 2);
  const isCompactOocyte = specimen.type === "oocyte";
  const isOocyteCohort =
    specimen.type === "oocyte" && specimen.oocyteRecordKind === "cohort";
  const oocyteCohortTitle = isOocyteCohort
    ? getOocyteCohortInventoryTitle(t, specimen)
    : specimen.label;
  const accessibilityLabel = [oocyteCohortTitle, ...detailRows].join(", ");

  if (isOocyteCohort) {
    return (
      <Pressable
        onPress={() => {
          capturePress(pressLabel(accessibilityLabel), "cryo_vault_action");
          onPress?.();
        }}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        style={({ pressed }) => ({
          width: "100%",
          borderRadius: 12,
          backgroundColor: pressed
            ? "rgba(255,255,255,0.68)"
            : "rgba(255,255,255,0.46)",
          borderWidth: 1,
          borderColor: "rgba(92,52,32,0.08)",
          padding: 12,
          gap: 10,
          opacity: pressed ? 0.82 : 1,
        })}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 9,
          }}
        >
          <SpecimenIcon specimen={specimen} size={28} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.72}
              style={{
                fontSize: 13,
                lineHeight: 16,
                fontWeight: "800",
                color: EMBER.colors.text,
              }}
            >
              {oocyteCohortTitle}
            </Text>
            {specimen.cycleLabel ? (
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  marginTop: 2,
                  fontSize: 11,
                  lineHeight: 14,
                  fontWeight: "600",
                  color: EMBER.colors.textLight,
                }}
              >
                {specimen.cycleLabel}
              </Text>
            ) : null}
          </View>
          <ChevronRight
            size={17}
            color={EMBER.colors.textLight}
            strokeWidth={1.7}
          />
        </View>
        <OocyteUnitGrid specimen={specimen} />
      </Pressable>
    );
  }

  if (isCompactOocyte) {
    return (
      <Pressable
        onPress={() => {
          capturePress(pressLabel(accessibilityLabel), "cryo_vault_action");
          onPress();
        }}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        style={({ pressed }) => ({
          width: 58,
          height: 62,
          borderRadius: 10,
          backgroundColor: pressed
            ? "rgba(255,255,255,0.62)"
            : "rgba(255,255,255,0.42)",
          borderWidth: 1,
          borderColor: "rgba(92,52,32,0.08)",
          paddingHorizontal: 6,
          paddingVertical: 7,
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
          opacity: pressed ? 0.78 : 1,
        })}
      >
        <SpecimenIcon specimen={specimen} size={24} />
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.72}
          style={{
            maxWidth: "100%",
            fontSize: 10.5,
            lineHeight: 12,
            fontWeight: "800",
            color: EMBER.colors.text,
            textAlign: "center",
          }}
        >
          {specimen.label}
        </Text>
      </Pressable>
    );
  }

  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(accessibilityLabel), "cryo_vault_action");
        onPress();
      }}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={({ pressed }) => ({
        width: "48%",
        minHeight: 82,
        borderRadius: 10,
        backgroundColor: pressed
          ? "rgba(255,255,255,0.62)"
          : "rgba(255,255,255,0.42)",
        borderWidth: 1,
        borderColor: "rgba(92,52,32,0.08)",
        paddingHorizontal: 10,
        paddingVertical: 10,
        justifyContent: "space-between",
        gap: 7,
        opacity: pressed ? 0.78 : 1,
      })}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 7,
        }}
      >
        <SpecimenIcon specimen={specimen} size={24} />
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.78}
          style={{
            flex: 1,
            fontSize: 12,
            lineHeight: 14,
            fontWeight: "800",
            color: EMBER.colors.text,
          }}
        >
          {specimen.label}
        </Text>
      </View>
      {detailRows.length > 0 && (
        <View style={{ gap: 2 }}>
          {detailRows.map((detail, index) => (
            <Text
              key={`${specimen.id}-detail-${index}`}
              className="font-dm-sans"
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.82}
              style={{
                fontSize: 10.5,
                lineHeight: 13,
                fontWeight: index === 0 ? "700" : "600",
                color: index === 0 ? EMBER.colors.text : EMBER.colors.textLight,
              }}
            >
              {detail}
            </Text>
          ))}
        </View>
      )}
    </Pressable>
  );
}

function NestedSpecimenGrid({
  items,
  onSelect,
}: {
  items: Specimen[];
  onSelect: (id: string) => void;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "flex-start",
      }}
    >
      {items.map((specimen) => (
        <NestedSpecimenTile
          key={specimen.id}
          specimen={specimen}
          onPress={() => onSelect(specimen.id)}
        />
      ))}
    </View>
  );
}

function GroupedSpecimenGrid({
  items,
  onSelect,
}: {
  items: Specimen[];
  onSelect: (id: string) => void;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const groups = useMemo(() => groupSpecimensByFacilityAndDate(items), [items]);

  return (
    <View style={{ gap: 18 }}>
      {groups.map((group, groupIndex) => {
        const specimenType = group.batches[0]?.items[0]?.type ?? "oocyte";
        const totalCount = group.batches.reduce(
          (sum, batch) => sum + getSpecimenInventoryTotal(batch.items),
          0,
        );
        const countLabel = getSpecimenCountLabel(t, specimenType, totalCount);
        const getBatchSubtitle = (batch: SpecimenDateBatch) => {
          const dateLabel = formatSpecimenDate(batch.frozenDate, locale);
          const batchCountLabel = getSpecimenCountLabel(
            t,
            specimenType,
            getSpecimenInventoryTotal(batch.items),
          );
          return dateLabel
            ? `${batchCountLabel} · ${t(
                "cryo_vault.grouped_inventory.frozen_date",
                {
                  params: { date: dateLabel },
                },
              )}`
            : batchCountLabel;
        };
        const onlyBatch = group.batches.length === 1 ? group.batches[0] : null;
        const facilitySubtitle = onlyBatch
          ? getBatchSubtitle(onlyBatch)
          : countLabel;

        return (
          <View
            key={`${group.facility || "unknown"}-${groupIndex}`}
            style={{
              ...CARD_STYLE,
              padding: 14,
              gap: 14,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
                paddingHorizontal: 2,
              }}
            >
              <OrganicMiniIcon kind="clinics" size={34} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <CardTitleText numberOfLines={1}>
                  {group.facility ||
                    t("cryo_vault.grouped_inventory.unknown_facility")}
                </CardTitleText>
                <CardSubtitleText numberOfLines={1}>
                  {facilitySubtitle}
                </CardSubtitleText>
              </View>
            </View>

            <View style={{ gap: 12 }}>
              {group.batches.map((batch) => {
                const batchSubtitle = getBatchSubtitle(batch);
                const showBatchSubtitle = group.batches.length > 1;
                return (
                  <View
                    key={`${group.facility || "unknown"}-${batch.frozenDate ?? "unknown"}`}
                    style={{ gap: 8 }}
                  >
                    {showBatchSubtitle ? (
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 6,
                          paddingHorizontal: 2,
                        }}
                      >
                        <Calendar
                          size={13}
                          color={EMBER.colors.textLight}
                          strokeWidth={1.6}
                        />
                        <CardSubtitleText
                          numberOfLines={1}
                          style={{ marginTop: 0, fontSize: 12 }}
                        >
                          {batchSubtitle}
                        </CardSubtitleText>
                      </View>
                    ) : null}
                    <NestedSpecimenGrid
                      items={batch.items}
                      onSelect={onSelect}
                    />
                  </View>
                );
              })}
            </View>
          </View>
        );
      })}
    </View>
  );
}

function RetrievalFunnelCard({
  funnel,
  onViewCyclePress,
}: {
  funnel: RetrievalFunnel;
  onViewCyclePress?: () => void;
}) {
  const capturePress = usePressCapture("RetrievalFunnelCard");
  const t = useTranslations();
  type FunnelStep = {
    label: string;
    subtitle: string;
    count: number;
    color: string;
  };

  const steps: FunnelStep[] = [
    {
      label: t("cryo_vault.funnel.retrieved"),
      subtitle: t("cryo_vault.funnel.retrieved_subtitle"),
      count: funnel.retrieved,
      color: "#86EFAC",
    },
  ];

  if (funnel.mature != null) {
    steps.push({
      label: t("cryo_vault.funnel.mature"),
      subtitle: t("cryo_vault.funnel.mature_subtitle"),
      count: funnel.mature,
      color: "#6EE7B7",
    });
  }
  if (funnel.fertilized != null) {
    steps.push({
      label: t("cryo_vault.funnel.fertilized"),
      subtitle: t("cryo_vault.funnel.fertilized_subtitle"),
      count: funnel.fertilized,
      color: "#38BDF8",
    });
  }
  if (funnel.blastocyst != null) {
    steps.push({
      label: t("cryo_vault.funnel.blastocysts"),
      subtitle: t("cryo_vault.funnel.blastocysts_subtitle"),
      count: funnel.blastocyst,
      color: "#818CF8",
    });
  }
  if (funnel.pgtTested != null) {
    steps.push({
      label: t("cryo_vault.funnel.pgt_tested"),
      subtitle: t("cryo_vault.funnel.pgt_tested_subtitle"),
      count: funnel.pgtTested,
      color: "#C084FC",
    });
  }
  if (funnel.euploid != null) {
    steps.push({
      label: t("cryo_vault.funnel.euploid"),
      subtitle: t("cryo_vault.funnel.euploid_subtitle"),
      count: funnel.euploid,
      color: EMBER.colors.green,
    });
  }
  steps.push({
    label: t("cryo_vault.funnel.frozen"),
    subtitle: t("cryo_vault.funnel.frozen_subtitle"),
    count: funnel.frozen,
    color: EMBER.colors.gold,
  });

  const rows: OutcomeDotFunnelStage[] = steps.map((step, index) => ({
    id: `${step.label}-${index}`,
    label: step.label,
    value: step.count,
    detail: step.subtitle,
  }));

  return (
    <View style={{ ...CARD_STYLE, padding: 16 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          fontWeight: "600",
          color: EMBER.colors.text,
          marginBottom: 4,
        }}
      >
        {funnel.cycleLabel}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          color: EMBER.colors.textLight,
          lineHeight: 17,
          marginBottom: 18,
        }}
      >
        {t("cryo_vault.funnel.subtitle")}
      </Text>

      <OutcomeDotFunnelRows
        stages={rows}
        totalDots={Math.max(funnel.retrieved, 1)}
        embedded
      />

      {/* View cycle details link */}
      {onViewCyclePress && (
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("cryo_vault.funnel.view_full_cycle")),
              "cryo_vault.funnel.view_full_cycle",
            );
            onViewCyclePress();
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            marginTop: 18,
            paddingVertical: 10,
            borderTopWidth: 1,
            borderTopColor: "rgba(0,0,0,0.04)",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "500",
              color: EMBER.colors.warm,
            }}
          >
            {t("cryo_vault.funnel.view_full_cycle")}
          </Text>
          <ChevronRight size={14} color={EMBER.colors.warm} strokeWidth={1.5} />
        </Pressable>
      )}
    </View>
  );
}

function FacilityCard({ facility }: { facility: FacilityInfo }) {
  const t = useTranslations();
  const countLabel =
    facility.specimenCount === 1
      ? t("cryo_vault.facility.specimen_count_one", {
          params: { count: String(facility.specimenCount) },
        })
      : t("cryo_vault.facility.specimen_count_other", {
          params: { count: String(facility.specimenCount) },
        });

  // TODO: when facility logos become available, render the logo image
  // here instead of the default clinic illustration.
  return (
    <View
      style={{
        flex: 1,
        ...CARD_STYLE,
        minHeight: 118,
        padding: 14,
        justifyContent: "space-between",
      }}
    >
      <OrganicMiniIcon kind="clinics" size={36} />

      <View style={{ flex: 1, minWidth: 0, marginTop: 12 }}>
        <CardTitleText numberOfLines={1}>{facility.name}</CardTitleText>
        <CardSubtitleText numberOfLines={1}>{countLabel}</CardSubtitleText>
      </View>
    </View>
  );
}

function DispositionCard({
  status,
  onDocumentsPress,
}: {
  status: DispositionStatus;
  onDocumentsPress?: () => void;
}) {
  const capturePress = usePressCapture("DispositionCard");
  const t = useTranslations();
  const isFiled = status === "filed";
  const isExpiring = status === "expiring_soon";

  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(
            t(
              isFiled
                ? "cryo_vault.disposition.on_file_title"
                : isExpiring
                  ? "cryo_vault.disposition.expiring_title"
                  : "cryo_vault.disposition.missing_title",
            ),
          ),
          "open_specimen_documents",
        );
        onDocumentsPress?.();
      }}
      style={{
        ...CARD_STYLE,
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        padding: 14,
      }}
    >
      {isFiled ? (
        <Shield size={16} color={EMBER.colors.green} strokeWidth={1.5} />
      ) : (
        <AlertCircle
          size={16}
          color={isExpiring ? EMBER.colors.gold : EMBER.colors.coral}
          strokeWidth={1.5}
        />
      )}
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "600",
            color: isFiled
              ? EMBER.colors.green
              : isExpiring
                ? EMBER.colors.gold
                : EMBER.colors.coral,
          }}
        >
          {isFiled
            ? t("cryo_vault.disposition.on_file_title")
            : isExpiring
              ? t("cryo_vault.disposition.expiring_title")
              : t("cryo_vault.disposition.missing_title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 15,
          }}
        >
          {isFiled
            ? t("cryo_vault.disposition.on_file_subtitle")
            : t("cryo_vault.disposition.missing_subtitle")}
        </Text>
      </View>
      <ChevronRight size={14} color={EMBER.colors.textFaint} />
    </Pressable>
  );
}

export function ActionCard({
  icon,
  label,
  subtitle,
  comingSoon,
  onPress,
}: {
  icon: React.ReactNode;
  label: string;
  subtitle: string;
  comingSoon?: boolean;
  onPress?: () => void;
}) {
  const capturePress = usePressCapture("ActionCard");
  return (
    <Pressable
      onPress={
        comingSoon
          ? undefined
          : () => {
              capturePress(pressLabel(label), "cryo_vault_nav_card");
              onPress?.();
            }
      }
      style={{
        ...CARD_STYLE,
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 15,
        paddingVertical: 13,
        opacity: comingSoon ? 0.65 : 1,
      }}
    >
      <View
        style={{
          width: 38,
          height: 38,
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <CardTitleText>{label}</CardTitleText>
          {comingSoon ? <StatusBadge type="coming-soon" /> : null}
        </View>
        <CardSubtitleText>{subtitle}</CardSubtitleText>
      </View>
      {!comingSoon && (
        <ChevronRight
          size={14}
          color={EMBER.colors.textLight}
          strokeWidth={1.8}
        />
      )}
    </Pressable>
  );
}

// The projection is a development-only preview until clinical review defines
// a model with documented inputs, provenance, uncertainty, and presentation.
function isCryoPotentialPreviewEnabled() {
  if (!__DEV__ || typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get("dev_cryo_potential") ===
    "true"
  );
}

type PotentialTabId = "combined" | "embryos" | "eggs";

function FertilityPotentialSection({ specimens }: { specimens: Specimen[] }) {
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState<PotentialTabId>("combined");
  if (!isCryoPotentialPreviewEnabled()) return null;

  const euploidEmbryos = specimens.filter(
    (s) => s.type === "embryo" && s.pgtResult === "euploid",
  ).length;
  const untestedEmbryos = specimens.filter(
    (s) =>
      s.type === "embryo" &&
      s.pgtResult !== "euploid" &&
      s.pgtResult !== "aneuploid",
  ).length;
  const frozenEggs = getSpecimenInventoryTotal(
    specimens.filter((s) => s.type === "oocyte"),
  );

  const embryoSteps = computeEmbryoPotential(euploidEmbryos, untestedEmbryos, {
    frozen: t("cryo_vault.potential.frozen_embryos"),
    thaw: t("cryo_vault.potential.survive_thaw"),
    implant: t("cryo_vault.potential.implant_successfully"),
    live: t("cryo_vault.potential.estimated_live_births"),
  });
  const eggSteps = computeEggPotential(frozenEggs, {
    frozen: t("cryo_vault.potential.frozen_eggs"),
    thaw: t("cryo_vault.potential.survive_thaw"),
    fertilize: t("cryo_vault.potential.fertilize"),
    blast: t("cryo_vault.potential.reach_blastocyst"),
    implant: t("cryo_vault.potential.implant_successfully"),
    live: t("cryo_vault.potential.estimated_live_births"),
  });
  const combinedSteps = computeCombinedPotential(embryoSteps, eggSteps);

  const hasEmbryos = embryoSteps.length > 0;
  const hasEggs = eggSteps.length > 0;

  if (!hasEmbryos && !hasEggs) return null;

  const potentialTabOptions: FunnelScopeTabOption<PotentialTabId>[] = [
    {
      id: "combined",
      label: t("cryo_vault.potential.tab_combined"),
      icon: Heart,
    },
    ...(hasEmbryos
      ? [
          {
            id: "embryos" as const,
            label: t("cryo_vault.potential.tab_embryos"),
            icon: Sparkles,
          },
        ]
      : []),
    ...(hasEggs
      ? [
          {
            id: "eggs" as const,
            label: t("cryo_vault.potential.tab_eggs"),
            icon: Egg,
          },
        ]
      : []),
  ];
  const activePotentialTab = potentialTabOptions.some(
    (option) => option.id === activeTab,
  )
    ? activeTab
    : "combined";
  const renderFunnel = (steps: PotentialStep[], type: PotentialTabId) => {
    const rows: OutcomeDotFunnelStage[] = steps.map((step, index) => ({
      id: `${step.label}-${index}`,
      label: step.label,
      value: step.count,
      detail:
        index === 0
          ? t("cryo_vault.potential.stored_cohort_detail")
          : type === "embryos"
            ? ([
                t("cryo_vault.potential.survive_thaw_detail"),
                t("cryo_vault.potential.implant_detail"),
                t("cryo_vault.potential.live_birth_detail"),
              ][index - 1] ?? t("cryo_vault.potential.live_birth_detail"))
            : ([
                t("cryo_vault.potential.survive_thaw_detail"),
                t("cryo_vault.potential.fertilize_detail"),
                t("cryo_vault.potential.reach_blastocyst_detail"),
                t("cryo_vault.potential.implant_detail"),
                t("cryo_vault.potential.live_birth_detail"),
              ][index - 1] ?? t("cryo_vault.potential.live_birth_detail")),
    }));

    return (
      <OutcomeDotFunnelRows
        stages={rows}
        totalDots={Math.max(steps[0]?.count ?? 1, 1)}
        embedded
      />
    );
  };

  const combinedLabelKeys: Record<CombinedPotentialStep["id"], string> = {
    inventory: "cryo_vault.potential.combined_inventory",
    thaw: "cryo_vault.potential.combined_thaw",
    "transfer-ready": "cryo_vault.potential.combined_transfer_ready",
    implant: "cryo_vault.potential.combined_implant",
    live: "cryo_vault.potential.combined_live_births",
  };
  const combinedDetailKeys: Record<CombinedPotentialStep["id"], string> = {
    inventory: "cryo_vault.potential.combined_inventory_detail",
    thaw: "cryo_vault.potential.combined_thaw_detail",
    "transfer-ready": "cryo_vault.potential.combined_transfer_ready_detail",
    implant: "cryo_vault.potential.combined_implant_detail",
    live: "cryo_vault.potential.combined_live_birth_detail",
  };
  const renderCombinedFunnel = (steps: CombinedPotentialStep[]) => {
    const embryoTotal = embryoSteps[0]?.count ?? 0;
    const eggTotal = eggSteps[0]?.count ?? 0;
    const rows: OutcomeDotFunnelStage[] = steps.map((step) => {
      const total = step.embryos + step.eggs;
      const displayValue =
        step.id === "live"
          ? String(total)
          : [step.embryos, step.eggs].filter((count) => count > 0).join("+") ||
            "0";

      return {
        id: `combined-${step.id}`,
        label: t(combinedLabelKeys[step.id]),
        value: total,
        displayValue,
        detail: t(combinedDetailKeys[step.id], {
          params: {
            embryos: String(step.embryos),
            eggs: String(step.eggs),
          },
        }),
        dotSegments: [
          {
            total: embryoTotal,
            active: step.embryos,
            color: SPECIMEN_CONFIG.embryo.color,
          },
          {
            total: eggTotal,
            active: step.eggs,
            color: SPECIMEN_CONFIG.oocyte.color,
          },
        ].filter((segment) => segment.total > 0),
      };
    });

    return (
      <OutcomeDotFunnelRows
        stages={rows}
        totalDots={Math.max(embryoTotal + eggTotal, 1)}
        embedded
      />
    );
  };

  return (
    <View style={{ marginTop: 28 }}>
      <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
        <SectionTitle icon={Heart}>
          {t("cryo_vault.potential.section_title")}
        </SectionTitle>
      </View>

      <FunnelScopeTabs
        options={potentialTabOptions}
        active={activePotentialTab}
        onChange={setActiveTab}
      />

      {/* The selected scope is the only control; the card starts with its funnel. */}
      <View
        style={{
          ...CARD_STYLE,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderTopWidth: 0,
          padding: 15,
          marginBottom: 10,
        }}
      >
        <View style={{ gap: 16 }}>
          {activePotentialTab === "combined" ? (
            renderCombinedFunnel(combinedSteps)
          ) : activePotentialTab === "embryos" && hasEmbryos ? (
            <View>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 12,
                }}
              >
                <View
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: SPECIMEN_CONFIG.embryo.color,
                  }}
                />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    fontWeight: "600",
                    color: EMBER.colors.textFaint,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {euploidEmbryos > 0
                    ? t("cryo_vault.potential.from_embryos_with_euploid", {
                        params: { count: String(euploidEmbryos) },
                      })
                    : t("cryo_vault.potential.from_embryos")}
                </Text>
              </View>
              {renderFunnel(embryoSteps, "embryos")}
            </View>
          ) : hasEggs ? (
            <View>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 12,
                }}
              >
                <View
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: SPECIMEN_CONFIG.oocyte.color,
                  }}
                />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    fontWeight: "600",
                    color: EMBER.colors.textFaint,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {t("cryo_vault.potential.from_frozen_eggs")}
                </Text>
              </View>
              {renderFunnel(eggSteps, "eggs")}
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
}

/** Single glass card with collapsible FAQ items */
function FAQSection() {
  const capturePress = usePressCapture("FAQSection");
  const t = useTranslations();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const items = [
    {
      question: t("cryo_vault.faq.expire_question"),
      answer: t("cryo_vault.faq.expire_answer"),
    },
    {
      question: t("cryo_vault.faq.quality_question"),
      answer: t("cryo_vault.faq.quality_answer"),
    },
    {
      question: t("cryo_vault.faq.eggs_needed_question"),
      answer: t("cryo_vault.faq.eggs_needed_answer"),
    },
    {
      question: t("cryo_vault.faq.move_facility_question"),
      answer: t("cryo_vault.faq.move_facility_answer"),
    },
  ];

  return (
    <View style={{ ...CARD_STYLE, padding: 0, overflow: "hidden" }}>
      {items.map((item, index) => {
        const isExpanded = expandedIndex === index;
        const isLast = index === items.length - 1;

        return (
          <Pressable
            key={index}
            onPress={() => {
              capturePress(pressLabel(item.question), "toggle_cryo_faq");
              setExpandedIndex(isExpanded ? null : index);
            }}
            style={{
              padding: 14,
              borderBottomWidth: isLast ? 0 : 1,
              borderBottomColor: "rgba(255,255,255,0.12)",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 14,
                  fontWeight: "800",
                  color: EMBER.colors.text,
                  lineHeight: 18,
                }}
              >
                {item.question}
              </Text>
              <ChevronDown
                size={14}
                color={EMBER.colors.textFaint}
                strokeWidth={1.5}
                style={{
                  transform: [{ rotate: isExpanded ? "180deg" : "0deg" }],
                }}
              />
            </View>
            {isExpanded && (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 18,
                  marginTop: 2,
                }}
              >
                {item.answer}
              </Text>
            )}
          </Pressable>
        );
      })}
    </View>
  );
}

function createCryoUploadFileFromWebFile(file: File): CryoUploadFile {
  const objectUrl = URL.createObjectURL(file);
  return {
    name: file.name || "Cryo report",
    uri: objectUrl,
    mimeType: resolveUploadContentType({
      name: file.name || "Cryo report",
      uri: objectUrl,
      mimeType: file.type,
    }),
    size: file.size,
    objectUrl,
  };
}

function getDroppedFiles(event: WebDragEvent) {
  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) return [];
  return Array.from(files);
}

function isWebDropZoneTarget(node: View | null): node is WebDropZoneTarget {
  if (!node || typeof node !== "object") return false;
  return (
    "addEventListener" in node &&
    typeof node.addEventListener === "function" &&
    "removeEventListener" in node &&
    typeof node.removeEventListener === "function"
  );
}

function revokeCryoUploadObjectUrl(file: CryoUploadFile | null) {
  if (Platform.OS !== "web" || !file?.objectUrl || typeof URL === "undefined") {
    return;
  }
  URL.revokeObjectURL(file.objectUrl);
}

function StaticCryoRailChip({
  label,
  icon: Icon,
}: {
  label: string;
  icon: LucideIcon;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 14,
        paddingVertical: 9,
        borderRadius: 20,
        borderWidth: 1,
        backgroundColor: "rgba(255, 255, 255, 0.42)",
        borderColor: "rgba(255, 255, 255, 0.58)",
      }}
    >
      <Icon size={14} color={EMBER.colors.textMid} style={{ marginRight: 6 }} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "400",
          color: EMBER.colors.textMid,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function MovingCryoChipRail({
  pills,
}: {
  pills: { label: string; icon: LucideIcon }[];
}) {
  const offset = useRef(new Animated.Value(0)).current;
  const [sequenceWidth, setSequenceWidth] = useState(0);

  useEffect(() => {
    if (sequenceWidth === 0) return;

    offset.setValue(0);
    const loop = Animated.loop(
      Animated.timing(offset, {
        toValue: -sequenceWidth,
        duration: Math.max(12000, sequenceWidth * 34),
        easing: Easing.linear,
        useNativeDriver: true,
      }),
      { resetBeforeIteration: true },
    );

    loop.start();
    return () => loop.stop();
  }, [offset, sequenceWidth]);

  const renderPillSet = (copyIndex: number, measure = false) => (
    <View
      key={copyIndex}
      onLayout={
        measure
          ? (event) => setSequenceWidth(event.nativeEvent.layout.width)
          : undefined
      }
      style={{
        flexDirection: "row",
        gap: 8,
        paddingRight: 8,
      }}
    >
      {pills.map((pill) => (
        <StaticCryoRailChip
          key={`${copyIndex}-${pill.label}`}
          label={pill.label}
          icon={pill.icon}
        />
      ))}
    </View>
  );

  return (
    <View style={{ width: "100%", overflow: "hidden", paddingHorizontal: 2 }}>
      <Animated.View
        style={{
          flexDirection: "row",
          transform: [{ translateX: offset }],
        }}
      >
        {renderPillSet(0, true)}
        {renderPillSet(1)}
      </Animated.View>
    </View>
  );
}

function CryoFileDropZone({
  onPick,
  onFileSelected,
}: {
  onPick: () => void;
  onFileSelected: (file: CryoUploadFile) => void;
}) {
  const capturePress = usePressCapture("CryoFileDropZone");
  const t = useTranslations();
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef<View | null>(null);

  useEffect(() => {
    if (Platform.OS !== "web") return;

    const node = dropZoneRef.current;
    if (!isWebDropZoneTarget(node)) return;

    const handleDragEnter = (event: WebDragEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      setIsDragging(true);
    };
    const handleDragOver = (event: WebDragEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      setIsDragging(true);
    };
    const handleDragLeave = (event: WebDragEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      setIsDragging(false);
    };
    const handleDrop = (event: WebDragEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      setIsDragging(false);
      const [file] = getDroppedFiles(event).map(
        createCryoUploadFileFromWebFile,
      );
      if (file) onFileSelected(file);
    };

    node.addEventListener("dragenter", handleDragEnter);
    node.addEventListener("dragover", handleDragOver);
    node.addEventListener("dragleave", handleDragLeave);
    node.addEventListener("drop", handleDrop);

    return () => {
      node.removeEventListener("dragenter", handleDragEnter);
      node.removeEventListener("dragover", handleDragOver);
      node.removeEventListener("dragleave", handleDragLeave);
      node.removeEventListener("drop", handleDrop);
    };
  }, [onFileSelected]);

  return (
    <Pressable
      ref={dropZoneRef}
      onPress={() => {
        capturePress(
          pressLabel(t("cryo_vault.empty.drop_title")),
          "cryo_vault.empty.drop_title",
        );
        onPick();
      }}
      style={{
        width: "100%",
        borderWidth: 1.5,
        borderColor: isDragging
          ? "rgba(122, 62, 21, 0.42)"
          : "rgba(122, 62, 21, 0.18)",
        borderStyle: "dashed",
        borderRadius: 14,
        paddingVertical: 26,
        paddingHorizontal: 18,
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        backgroundColor: isDragging
          ? "rgba(122, 62, 21, 0.06)"
          : "rgba(255,255,255,0.22)",
      }}
    >
      <Upload size={24} color={EMBER.colors.warm} strokeWidth={1.6} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          lineHeight: 19,
          fontWeight: "700",
          color: EMBER.colors.text,
          textAlign: "center",
        }}
      >
        {t("cryo_vault.empty.drop_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 17,
          color: EMBER.colors.textFaint,
          textAlign: "center",
        }}
      >
        {t("cryo_vault.empty.drop_hint")}
      </Text>
    </Pressable>
  );
}

function EmptyVault({
  onUpload,
  onFileSelected,
}: {
  onUpload: () => void;
  onFileSelected: (file: CryoUploadFile) => void;
}) {
  const t = useTranslations();
  const emptyPills = [
    { label: t("cryo_vault.empty.chip_eggs"), icon: Egg },
    { label: t("cryo_vault.empty.chip_embryos"), icon: Sparkles },
    { label: t("cryo_vault.empty.chip_sperm"), icon: Droplet },
    { label: t("cryo_vault.empty.chip_freeze_reports"), icon: FileText },
    { label: t("cryo_vault.empty.chip_storage"), icon: Shield },
    { label: t("cryo_vault.empty.chip_facility"), icon: MapPin },
    { label: t("cryo_vault.empty.chip_disposition"), icon: Heart },
  ];

  return (
    <View
      style={{
        paddingVertical: 8,
        alignItems: "center",
        overflow: "visible",
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          lineHeight: 27,
          fontWeight: "700",
          color: EMBER.colors.text,
          textAlign: "center",
        }}
      >
        {t("cryo_vault.empty.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: EMBER.colors.textMid,
          textAlign: "center",
          lineHeight: 21,
          marginTop: 8,
          maxWidth: 410,
        }}
      >
        {t("cryo_vault.empty.body")}
      </Text>
      <View
        style={{
          width: "100%",
          marginTop: 22,
          marginBottom: 18,
        }}
      >
        <MovingCryoChipRail pills={emptyPills} />
      </View>
      <CryoFileDropZone onPick={onUpload} onFileSelected={onFileSelected} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          color: EMBER.colors.textFaint,
          textAlign: "center",
          lineHeight: 17,
          marginTop: 12,
          maxWidth: 420,
        }}
      >
        {t("cryo_vault.empty.note")}
      </Text>
    </View>
  );
}

function EmbryoTransferSelectionDialog({
  visible,
  embryos,
  onClose,
  onContinue,
}: {
  visible: boolean;
  embryos: Specimen[];
  onClose: () => void;
  onContinue: (selected: Specimen[]) => void;
}) {
  const capturePress = usePressCapture("EmbryoTransferSelectionDialog");
  const t = useTranslations();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (visible) setSelectedIds(new Set());
  }, [visible]);

  const selected = embryos.filter((embryo) => selectedIds.has(embryo.id));

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout="centered"
      closeButton="minimal"
      containerStyle={{ maxHeight: 720 }}
      surfaceStyle={{ maxHeight: 720 }}
      contentStyle={{ paddingHorizontal: 20, paddingBottom: 18 }}
      testID="embryo-transfer-selection-dialog"
    >
      <DialogHeader
        title={t("cryo_vault.transfer_selection.title")}
        subtitle={t("cryo_vault.transfer_selection.subtitle")}
        style={{ marginBottom: 14 }}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ maxHeight: 430 }}
        contentContainerStyle={{ gap: 8, paddingBottom: 4 }}
      >
        {embryos.map((embryo) => {
          const isSelected = selectedIds.has(embryo.id);
          return (
            <Pressable
              key={embryo.id}
              onPress={() => {
                capturePress(pressLabel(embryo.label), "select_embryo");
                (() =>
                  setSelectedIds((current) => {
                    const next = new Set(current);
                    if (next.has(embryo.id)) next.delete(embryo.id);
                    else next.add(embryo.id);
                    return next;
                  }))();
              }}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: isSelected }}
              accessibilityLabel={embryo.label}
              style={({ pressed }) => ({
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                padding: 12,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: isSelected
                  ? "rgba(59,26,8,0.2)"
                  : "rgba(0,0,0,0.08)",
                backgroundColor: isSelected
                  ? "rgba(59,26,8,0.06)"
                  : pressed
                    ? "rgba(0,0,0,0.03)"
                    : "transparent",
              })}
            >
              <View
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  alignItems: "center",
                  justifyContent: "center",
                  borderWidth: 1,
                  borderColor: isSelected
                    ? EMBER.colors.warm
                    : "rgba(0,0,0,0.16)",
                  backgroundColor: isSelected
                    ? EMBER.colors.warm
                    : "transparent",
                }}
              >
                {isSelected ? (
                  <Check size={15} color="#FFFFFF" strokeWidth={2.5} />
                ) : null}
              </View>
              <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
                <Text
                  className="font-dm-sans"
                  numberOfLines={1}
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                  }}
                >
                  {embryo.label}
                </Text>
                <Text
                  className="font-dm-sans"
                  numberOfLines={2}
                  style={{
                    fontSize: 11,
                    lineHeight: 15,
                    color: EMBER.colors.textLight,
                  }}
                >
                  {[embryo.facility, embryo.cycleLabel, embryo.pgtResult]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              </View>
            </Pressable>
          );
        })}
      </ScrollView>
      <View style={{ flexDirection: "row", gap: 10, marginTop: 16 }}>
        <SoftSecondaryButton
          label={t("cryo_vault.transfer_selection.cancel")}
          onPress={onClose}
          flex={0.8}
        />
        <DialogPrimaryButton
          label={t("cryo_vault.transfer_selection.continue")}
          onPress={() => onContinue(selected)}
          disabled={selected.length === 0}
          flex={1.2}
        />
      </View>
    </Dialog>
  );
}

// ── Screen ──

type Props = NativeStackScreenProps<CareStackParamList, "CryoVault">;

export default function CryoVaultScreen({ navigation, route }: Props) {
  const capturePress = usePressCapture("CryoVaultScreen");
  const handleBack = useSmartBack({
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const t = useTranslations();
  const { locale } = useLocale();
  const devCryoVaultScenario = getDevCryoVaultScenario();
  const shouldOpenAddEmbryosSurface = shouldOpenDevAddEmbryosSurface();
  const specimensQuery = useSpecimens();
  const deleteSpecimen = useDeleteSpecimen();
  const {
    data: apiSpecimens,
    isLoading: specimensLoading,
    error: specimensError,
  } = specimensQuery;
  const cohortsQuery = useOocyteCohorts();
  const {
    data: apiOocyteCohorts,
    isLoading: cohortsLoading,
    error: cohortsError,
  } = cohortsQuery;
  const documentsQuery = useDocuments();
  const {
    data: documents,
    isLoading: documentsLoading,
    error: documentsError,
  } = documentsQuery;
  const devClinicalDataState = getDevClinicalDataState("cryo");
  const coreCryoError = specimensError ?? cohortsError;
  const hasResolvedCryoSource =
    apiSpecimens !== undefined || apiOocyteCohorts !== undefined;
  const cryoDataState = devClinicalDataState
    ? devClinicalDataState
    : devCryoVaultScenario
      ? null
      : coreCryoError && !hasResolvedCryoSource
        ? "unavailable"
        : coreCryoError || documentsError
          ? "stale"
          : null;
  const isRetryingCryo =
    specimensQuery.isFetching ||
    cohortsQuery.isFetching ||
    documentsQuery.isFetching;
  const retryCryo = () => {
    void specimensQuery.refetch();
    void cohortsQuery.refetch();
    void documentsQuery.refetch();
  };
  const specimens = useMemo(() => {
    const buildLabel = (apiType: string, n: number) => {
      return buildCryoSpecimenLabel(t, apiType, n);
    };

    if (devCryoVaultScenario === "oocyte_batches") {
      return buildDevCryoVaultSpecimens(buildLabel);
    }
    if (devCryoVaultScenario === "specimen_modal") {
      return buildDevSpecimenModalSpecimens(buildLabel);
    }

    const cohorts = mapApiOocyteCohortsToCryoDetails(apiOocyteCohorts ?? []);
    const cohortMemberIds = new Set(
      (apiOocyteCohorts ?? []).flatMap((cohort) =>
        cohort.members.map((member) => member.id),
      ),
    );
    const individualSpecimens = (apiSpecimens ?? []).filter(
      (specimen) => !cohortMemberIds.has(specimen.id),
    );
    return [
      ...mapApiSpecimensToCryoDetails(individualSpecimens, buildLabel),
      ...cohorts,
    ];
  }, [apiOocyteCohorts, apiSpecimens, devCryoVaultScenario, t]);
  const displaySpecimensLoading = devCryoVaultScenario
    ? false
    : specimensLoading || cohortsLoading;
  const displayDocumentsLoading = devCryoVaultScenario
    ? false
    : documentsLoading;
  const cryoDocumentRelationTags = useMemo(
    () => getCryoDocumentRelationTags(specimens),
    [specimens],
  );
  const cryoDocuments = useMemo(
    () =>
      (documents ?? []).filter((document) =>
        isCryoRelatedDocument(document, cryoDocumentRelationTags),
      ),
    [cryoDocumentRelationTags, documents],
  );

  const facilities = useMemo((): FacilityInfo[] => {
    const map = new Map<string, FacilityInfo>();
    for (const s of specimens.filter(isVerifiedStoredSpecimen)) {
      if (!s.facility) continue;
      const existing = map.get(s.facility);
      if (existing) {
        existing.specimenCount++;
        if (
          s.frozenDate &&
          (!existing.storageStartDate ||
            s.frozenDate < existing.storageStartDate)
        ) {
          existing.storageStartDate = s.frozenDate;
        }
      } else {
        map.set(s.facility, {
          name: s.facility,
          specimenCount: 1,
          storageStartDate: s.frozenDate,
        });
      }
    }
    return Array.from(map.values());
  }, [specimens]);

  const funnels: RetrievalFunnel[] = [];
  const dispositionStatus: DispositionStatus | null = null;

  const [selectedSpecimen, setSelectedSpecimen] = useState<string | null>(
    route.params?.specimenId ?? null,
  );
  const [addSpecimenVisible, setAddSpecimenVisible] = useState(false);
  const [pendingSpecimenFile, setPendingSpecimenFile] =
    useState<CryoUploadFile | null>(null);
  const [transferSelectionVisible, setTransferSelectionVisible] =
    useState(false);
  const { openStartTreatment: openStartTreatmentModal } =
    useStartTreatmentController();
  const resolveCycleNavigation = useCycleNavigation();

  useEffect(() => {
    if (shouldOpenAddEmbryosSurface) {
      setAddSpecimenVisible(true);
    }
  }, [devCryoVaultScenario, shouldOpenAddEmbryosSurface]);

  useEffect(() => {
    if (route.params?.specimenId) {
      setSelectedSpecimen(route.params.specimenId);
    }
  }, [route.params?.specimenId]);

  const handleCloseSpecimen = () => {
    setSelectedSpecimen(null);
    if (route.params?.specimenId) {
      navigation.setParams({ specimenId: undefined });
    }
  };

  const handleDeleteSpecimen = async (specimen: CryoSpecimenDetail) => {
    await deleteSpecimen.mutateAsync(specimen.id);
    handleCloseSpecimen();
  };

  const verifiedSpecimens = specimens.filter(isVerifiedStoredSpecimen);
  const embryos = verifiedSpecimens.filter((s) => s.type === "embryo");
  const eggs = verifiedSpecimens.filter((s) => s.type === "oocyte");
  const sperm = verifiedSpecimens.filter((s) => s.type === "sperm");

  const hasEmbryos = embryos.length > 0;
  const hasEggs = eggs.length > 0;

  const sections = [
    embryos.length > 0 && { type: "embryo" as SpecimenType, items: embryos },
    eggs.length > 0 && { type: "oocyte" as SpecimenType, items: eggs },
    sperm.length > 0 && { type: "sperm" as SpecimenType, items: sperm },
  ].filter(Boolean) as { type: SpecimenType; items: Specimen[] }[];

  const handleDocumentsPress = () => {
    navigation.navigate("Documents", { initialCategory: "legal_consent" });
  };

  const handleCryoDocumentUpload = () => {
    navigation.navigate("Documents", { openUpload: true });
  };

  const openAddSpecimen = useCallback(() => {
    setPendingSpecimenFile(null);
    setAddSpecimenVisible(true);
  }, []);

  const openAddSpecimenWithFile = useCallback((file: CryoUploadFile) => {
    setPendingSpecimenFile((previous) => {
      revokeCryoUploadObjectUrl(previous);
      return file;
    });
    setAddSpecimenVisible(true);
  }, []);

  const closeAddSpecimen = useCallback(() => {
    setAddSpecimenVisible(false);
    setPendingSpecimenFile((previous) => {
      revokeCryoUploadObjectUrl(previous);
      return null;
    });
  }, []);

  const openStartTreatment = (frozenPrefill: FrozenPrefill | null = null) => {
    openStartTreatmentModal({
      frozenPrefill,
      source: "CryoVaultScreen",
      // Replace so the back button lands on CareOverview, not on the empty
      // CryoVault the user just left.
      onCreated: (createdCycle) =>
        navigation.replace("CycleDetail", { cycleId: createdCycle?.id }),
      onOpenActiveCycle: (cycle) =>
        navigation.replace("CycleDetail", { cycleId: cycle.id }),
    });
  };

  const handleEmbryoTransferSelection = (selected: Specimen[]) => {
    setTransferSelectionVisible(false);
    openStartTreatment({
      type: "embryo",
      specimenIds: selected.map((specimen) => specimen.id),
      specimenLabels: selected.map((specimen) => specimen.label),
    });
  };

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back */}
        <Pressable
          onPress={() => {
            capturePress(pressLabel(t("cryo_vault.back")), "cryo_vault.back");
            handleBack();
          }}
          className="mb-4 flex-row items-center gap-1"
        >
          <ChevronLeft size={18} color={EMBER.colors.warm} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 14, color: EMBER.colors.warm }}
          >
            {t("cryo_vault.back")}
          </Text>
        </Pressable>

        <ScreenHeader
          title={t("cryo_vault.title")}
          subtitle={t("cryo_vault.subtitle")}
          right={
            cryoDataState === "unavailable" ? undefined : (
              <Pressable
                onPress={() => {
                  capturePress(pressLabel("Add specimen"), "open_add_specimen");
                  openAddSpecimen();
                }}
                accessibilityRole="button"
                accessibilityLabel="Add specimen"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 18,
                  backgroundColor: `${EMBER.colors.warm}14`,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Plus size={18} color={EMBER.colors.warm} strokeWidth={2} />
              </Pressable>
            )
          }
        />

        {cryoDataState === "stale" ? (
          <ClinicalDataStatus
            state="stale"
            message={t("clinical_data.stale_body")}
            retryLabel={t("clinical_data.retry")}
            retryingLabel={t("clinical_data.retrying")}
            onRetry={retryCryo}
            isRetrying={isRetryingCryo}
            style={{ marginBottom: 12 }}
            testID="cryo-vault-stale"
          />
        ) : null}

        {cryoDataState === "unavailable" ? (
          <ClinicalDataStatus
            state="unavailable"
            title={t("clinical_data.unavailable_title")}
            message={t("clinical_data.unavailable_body")}
            retryLabel={t("clinical_data.retry")}
            retryingLabel={t("clinical_data.retrying")}
            onRetry={retryCryo}
            isRetrying={isRetryingCryo}
            testID="cryo-vault-unavailable"
          />
        ) : displaySpecimensLoading ? (
          <View style={{ paddingVertical: 60, alignItems: "center" }}>
            <ActivityIndicator size="large" color={EMBER.colors.warm} />
          </View>
        ) : specimens.length === 0 ? (
          <EmptyVault
            onUpload={openAddSpecimen}
            onFileSelected={openAddSpecimenWithFile}
          />
        ) : (
          <>
            {/* Summary */}
            <CryoSummary specimens={specimens} />

            {/* Specimen sections as tile grids */}
            {sections.map((section) => {
              const config = SPECIMEN_CONFIG[section.type];
              const sectionCount = getSpecimenInventoryTotal(section.items);
              const hasFacilityDetails = section.items.some((item) =>
                item.facility.trim(),
              );
              return (
                <View key={section.type} style={{ marginTop: 24 }}>
                  <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
                    <SectionTitle icon={config.sectionIcon}>
                      {`${sectionCount} ${
                        sectionCount === 1
                          ? t(config.singularKey)
                          : t(config.pluralKey)
                      }`}
                    </SectionTitle>
                  </View>
                  {hasFacilityDetails ? (
                    <GroupedSpecimenGrid
                      items={section.items}
                      onSelect={(id) => setSelectedSpecimen(id)}
                    />
                  ) : (
                    <SpecimenGrid
                      items={section.items}
                      onSelect={(id) => setSelectedSpecimen(id)}
                    />
                  )}
                </View>
              );
            })}

            {/* Fertility potential — cryo to live birth projection */}
            <FertilityPotentialSection specimens={verifiedSpecimens} />

            {/* Actions */}
            <View style={{ marginTop: 28 }}>
              <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
                <SectionTitle icon={Sparkles}>
                  {t("cryo_vault.sections.what_to_do")}
                </SectionTitle>
              </View>
              <View style={{ gap: 8 }}>
                {hasEmbryos && (
                  <ActionCard
                    icon={<OrganicMiniIcon kind="genetics" size={36} />}
                    label={t("cryo_vault.actions.plan_transfer_label")}
                    subtitle={t("cryo_vault.actions.plan_transfer_subtitle")}
                    onPress={() => setTransferSelectionVisible(true)}
                  />
                )}
                {hasEggs && (
                  <ActionCard
                    icon={<OrganicMiniIcon kind="eggs" size={36} />}
                    label={t("cryo_vault.actions.fertilize_label")}
                    subtitle={t("cryo_vault.actions.fertilize_subtitle")}
                    onPress={() => {
                      const eggIds = eggs.map((e) => e.id);
                      openStartTreatment({
                        type: "oocyte",
                        specimenIds: eggIds,
                      });
                    }}
                  />
                )}
                <ActionCard
                  icon={<OrganicMiniIcon kind="start" size={36} />}
                  label={t("cryo_vault.actions.start_cycle_label")}
                  subtitle={t("cryo_vault.actions.start_cycle_subtitle")}
                  onPress={() => openStartTreatment()}
                />
                <ActionCard
                  icon={
                    <Truck
                      size={22}
                      color={EMBER.colors.textLight}
                      strokeWidth={1.5}
                    />
                  }
                  label={t("cryo_vault.actions.move_label")}
                  subtitle={t("cryo_vault.actions.move_subtitle")}
                  comingSoon
                />
              </View>
            </View>

            {/* Retrieval funnels */}
            {funnels.length > 0 && (
              <View style={{ marginTop: 28 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 16,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                    marginBottom: 10,
                    paddingHorizontal: 2,
                  }}
                >
                  {t("cryo_vault.sections.retrieval_results")}
                </Text>
                <View style={{ gap: 10 }}>
                  {funnels.map((f) => (
                    <RetrievalFunnelCard
                      key={f.cycleLabel}
                      funnel={f}
                      onViewCyclePress={() => {
                        if (f.cycleId) {
                          navigation.navigate("PastCycleDetail", {
                            cycleId: f.cycleId,
                          });
                        }
                      }}
                    />
                  ))}
                </View>
              </View>
            )}

            {/* Facilities */}
            {facilities.length > 0 && (
              <View style={{ marginTop: 28 }}>
                <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
                  <SectionTitle icon={MapPin}>
                    {t("cryo_vault.sections.storage_facilities")}
                  </SectionTitle>
                </View>
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 10,
                  }}
                >
                  {facilities.map((f) => (
                    <View
                      key={f.name}
                      style={{
                        flexGrow: 1,
                        flexBasis: "22%",
                        minWidth: 130,
                      }}
                    >
                      <FacilityCard facility={f} />
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Disposition */}
            {dispositionStatus && (
              <View style={{ marginTop: 24 }}>
                <DispositionCard
                  status={dispositionStatus}
                  onDocumentsPress={handleDocumentsPress}
                />
              </View>
            )}

            {/* Related documents */}
            <View style={{ marginTop: 28 }}>
              <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
                <SectionTitle icon={FileText}>
                  {t("cryo_vault.sections.related_documents")}
                </SectionTitle>
              </View>
              {displayDocumentsLoading ? (
                <View>
                  {Array.from({ length: 2 }).map((_, index) => (
                    <ListRowSkeleton
                      key={index}
                      showLeadingSquare
                      showSubtitle
                      showTrailing
                    />
                  ))}
                </View>
              ) : cryoDocuments.length > 0 ? (
                <View style={{ gap: 8 }}>
                  {cryoDocuments.map((document) => (
                    <DocumentRow
                      key={document.id}
                      title={document.name}
                      dateLabel={
                        formatSpecimenDate(
                          document.documentDate ?? document.createdAt,
                          locale,
                        ) ?? undefined
                      }
                      icon={
                        <FileText
                          size={18}
                          color={EMBER.colors.textLight}
                          strokeWidth={1.5}
                        />
                      }
                      status={getDocumentRowStatus(document)}
                      onPress={() =>
                        navigation.navigate("Documents", {
                          initialDocumentId: document.id,
                        })
                      }
                    />
                  ))}
                </View>
              ) : (
                <DocumentRow
                  title={t("care.cycle_documents.empty_title")}
                  subtitle={t("care.cycle_documents.empty_subtitle")}
                  icon={<OrganicMiniIcon kind="medical-records" size={34} />}
                  trailing={
                    <Plus
                      size={18}
                      color={EMBER.colors.textLight}
                      strokeWidth={1.5}
                    />
                  }
                  onPress={handleCryoDocumentUpload}
                  showChevron={false}
                />
              )}
            </View>

            {/* FAQ */}
            <View style={{ marginTop: 28, marginBottom: 8 }}>
              <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
                <SectionTitle icon={Info}>
                  {t("cryo_vault.sections.common_questions")}
                </SectionTitle>
              </View>
              <FAQSection />
            </View>
          </>
        )}
      </ScrollView>

      <CryoSpecimenDetailModal
        specimen={specimens.find((s) => s.id === selectedSpecimen) ?? null}
        allSpecimens={specimens}
        visible={selectedSpecimen != null}
        onClose={handleCloseSpecimen}
        onSelectSpecimen={setSelectedSpecimen}
        onDelete={handleDeleteSpecimen}
        onPlanTreatment={(specimen) => {
          if (
            specimen.type !== "sperm" &&
            specimen.type !== "embryo" &&
            specimen.type !== "oocyte"
          ) {
            return;
          }
          handleCloseSpecimen();
          openStartTreatment({
            type: specimen.type === "sperm" ? "semen" : specimen.type,
            specimenIds: [specimen.id],
            specimenLabels: [specimen.label],
          });
        }}
        getCycleNavigation={(cycleId) => {
          const navigate = resolveCycleNavigation(cycleId);
          if (!navigate) return undefined;
          return () => {
            handleCloseSpecimen();
            navigate();
          };
        }}
        getEmbryoReportNavigation={(cycleId) => () => {
          handleCloseSpecimen();
          navigation.navigate("EmbryoReport", { cycleId });
        }}
      />
      <EmbryoTransferSelectionDialog
        visible={transferSelectionVisible}
        embryos={embryos}
        onClose={() => setTransferSelectionVisible(false)}
        onContinue={handleEmbryoTransferSelection}
      />
      <AddSpecimenModal
        visible={addSpecimenVisible}
        onClose={closeAddSpecimen}
        prefill={getDevAddEmbryosPrefill()}
        initialFile={pendingSpecimenFile}
      />
    </GlassScreenLayout>
  );
}
